import { FOCUS_RING } from "../styles";
import {
	type ChartData,
	type ChartRow,
	seqColors,
	seqStep,
	seriesColor,
	valueText,
	xText,
} from "./data";

/**
 * The charts that need no axis (EP13-S04): a funnel, a gauge, a heat matrix
 * and a stacked meter. Plain HTML and one SVG arc, laid out with logical
 * properties, so they mirror in Arabic with the document; every figure is
 * written beside its mark, so none of them relies on colour alone.
 */

function numberOf(row: ChartRow, key: string): number {
	const value = row[key];
	return typeof value === "number" ? value : 0;
}

/** The share of a whole as a width, clamped to the bar. */
export function widthPct(value: number, whole: number): string {
	if (whole <= 0) {
		return "0%";
	}
	return `${Math.min(100, Math.max(0, (value / whole) * 100)).toFixed(1)}%`;
}

export interface FunnelProps extends ChartData {
	/** Formats a stage-to-stage conversion, a fraction 0 to 1. */
	formatShare: (fraction: number) => string;
}

/**
 * Stages top to bottom, each bar as wide as its share of the first stage, with
 * its count and its conversion from the stage before. Reads the first series.
 */
export function Funnel({ formatShare, ...data }: FunnelProps) {
	const series = data.series[0];
	if (series === undefined) {
		return null;
	}
	const first = numberOf(data.rows[0] ?? {}, series.key);
	return (
		<ol className="flex flex-col gap-2">
			{data.rows.map((row, index) => {
				const value = numberOf(row, series.key);
				const previous = index === 0 ? null : numberOf(data.rows[index - 1] ?? {}, series.key);
				return (
					// biome-ignore lint/suspicious/noArrayIndexKey: stages have no identity beyond their order
					<li key={index} className="flex flex-col gap-1">
						<div className="flex items-baseline justify-between gap-2 text-[12px]">
							<span className="font-semibold text-fg">{xText(data, row)}</span>
							<span className="text-fg-muted [font-variant-numeric:tabular-nums]">
								{valueText(data, row[series.key])}
								{previous !== null && previous > 0 && ` · ${formatShare(value / previous)}`}
							</span>
						</div>
						<div className="h-3 rounded-sm" style={{ background: "var(--viz-grid)" }}>
							<div
								className="h-full rounded-sm"
								style={{ width: widthPct(value, first), background: seriesColor(series, 0) }}
							/>
						</div>
					</li>
				);
			})}
		</ol>
	);
}

export interface GaugeProps {
	value: number;
	max: number;
	/** The figure written under the arc. */
	label: string;
	/** A tick on the arc, 0 to `max`. */
	target?: number;
	size?: number;
}

/** Where a fraction of a half circle lands on a 100×50 arc of radius 40. */
export function arcPoint(fraction: number): { x: number; y: number } {
	const angle = Math.PI * (1 - Math.min(1, Math.max(0, fraction)));
	return { x: 50 + 40 * Math.cos(angle), y: 50 - 40 * Math.sin(angle) };
}

/** A half-circle meter, filling from the reading start; the figure is text below it. */
export function Gauge({ value, max, label, target, size = 160 }: GaugeProps) {
	const fraction = max > 0 ? value / max : 0;
	const end = arcPoint(fraction);
	const tick = target === undefined || max <= 0 ? null : arcPoint(target / max);
	return (
		<div className="flex flex-col items-center gap-1" style={{ width: size }}>
			{/* Mirrored in Arabic so the arc fills from the reading start. */}
			<svg viewBox="0 0 100 56" className="w-full rtl:-scale-x-100" aria-hidden="true">
				<path d="M10 50 A40 40 0 0 1 90 50" fill="none" stroke="var(--viz-grid)" strokeWidth={9} />
				{fraction > 0 && (
					<path
						d={`M10 50 A40 40 0 0 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`}
						fill="none"
						stroke="var(--viz-cat-1)"
						strokeWidth={9}
					/>
				)}
				{tick !== null && (
					<circle cx={tick.x} cy={tick.y} r={3} fill="var(--viz-target)" stroke="none" />
				)}
			</svg>
			<p className="font-extrabold text-[18px] text-fg [font-variant-numeric:tabular-nums]">
				{label}
			</p>
		</div>
	);
}

export interface HeatMatrixProps extends ChartData {
	/** A cell's second line under its figure, e.g. its count: `n = 3`. */
	sub?: (row: ChartRow, key: string) => string | undefined;
	/** More about a cell, shown on hover and read with it: median and p90. */
	describe?: (row: ChartRow, key: string) => string | undefined;
	/** Makes each filled cell a button: the city drawer opens from it. */
	onSelect?: (row: ChartRow, key: string) => void;
}

/**
 * Rows × columns of cells on the sequential ramp, each with its figure: the
 * company × governorate time-to-deliver matrix. The category is the row, each
 * series a column. It is a table already, so it is drawn as one.
 *
 * With `onSelect` a filled cell is a button, so Tab walks the matrix and
 * Enter opens it; its figure stays in the text, never in the colour alone.
 */
export function HeatMatrix({ sub, describe, onSelect, ...data }: HeatMatrixProps) {
	const values = data.rows.flatMap((row) =>
		data.series.map((series) => row[series.key]).filter((v): v is number => typeof v === "number"),
	);
	const min = Math.min(...values);
	const max = Math.max(...values);
	return (
		<div className="overflow-auto">
			<table className="w-full border-separate border-spacing-0.5 text-[12px]">
				<thead>
					<tr>
						<th scope="col" className="px-2 py-1 text-start font-semibold text-fg-subtle">
							{data.x.label}
						</th>
						{data.series.map((series) => (
							<th key={series.key} scope="col" className="px-2 py-1 font-semibold text-fg-subtle">
								{series.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{data.rows.map((row, index) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity beyond their order
						<tr key={index}>
							<th scope="row" className="px-2 py-1 text-start font-medium text-fg">
								{xText(data, row)}
							</th>
							{data.series.map((series) => {
								const value = row[series.key];
								const filled = typeof value === "number";
								const line = filled ? sub?.(row, series.key) : undefined;
								const more = filled ? describe?.(row, series.key) : undefined;
								const figure = (
									<>
										<span className="block">{valueText(data, value)}</span>
										{line !== undefined && (
											<span className="block font-normal text-[11px]">{line}</span>
										)}
									</>
								);
								return (
									<td
										key={series.key}
										title={more}
										className="rounded-sm px-2 py-1.5 text-center font-semibold [font-variant-numeric:tabular-nums]"
										style={
											filled
												? seqColors(seqStep(value, min, max))
												: { background: "var(--bg-subtle)", color: "var(--fg-muted)" }
										}
									>
										{filled && onSelect !== undefined ? (
											<button
												type="button"
												className={`w-full cursor-pointer rounded-sm font-semibold ${FOCUS_RING}`}
												aria-label={[
													xText(data, row),
													series.label,
													valueText(data, value),
													line,
													more,
												]
													.filter(Boolean)
													.join(", ")}
												onClick={() => onSelect(row, series.key)}
											>
												{figure}
											</button>
										) : (
											figure
										)}
									</td>
								);
							})}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export interface StackedMeterProps extends ChartData {
	/** The row field holding each row's capacity. */
	totalKey: string;
	/** A line across every bar at this fraction of capacity: 0.85. */
	marker?: number;
	/** Formats the filled share, a fraction 0 to 1. */
	formatShare: (fraction: number) => string;
}

/**
 * One bar per row (a unit kind), its series stacked against the row's
 * capacity, with the filled share written at the end and an optional marker.
 * A row past the marker is written in bold as well as crossing the line.
 */
export function StackedMeter({ totalKey, marker, formatShare, ...data }: StackedMeterProps) {
	return (
		<ul className="flex flex-col gap-2.5">
			{data.rows.map((row, index) => {
				const total = numberOf(row, totalKey);
				const filled = data.series.reduce((sum, series) => sum + numberOf(row, series.key), 0);
				const share = total > 0 ? filled / total : 0;
				const over = marker !== undefined && share > marker;
				return (
					// biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity beyond their order
					<li key={index} className="flex flex-col gap-1">
						<div className="flex items-baseline justify-between gap-2 text-[12px]">
							<span className="font-semibold text-fg">{xText(data, row)}</span>
							<span
								className={
									over
										? "font-extrabold text-fg [font-variant-numeric:tabular-nums]"
										: "text-fg-muted [font-variant-numeric:tabular-nums]"
								}
							>
								{formatShare(share)}
							</span>
						</div>
						<div
							className="relative flex h-3 overflow-hidden rounded-sm"
							style={{ background: "var(--viz-grid)" }}
						>
							{data.series.map((series, position) => (
								<div
									key={series.key}
									className="h-full"
									style={{
										width: widthPct(numberOf(row, series.key), total),
										background: seriesColor(series, position),
									}}
								/>
							))}
							{marker !== undefined && (
								<span
									aria-hidden="true"
									className="absolute inset-y-0 w-0.5"
									style={{ insetInlineStart: widthPct(marker, 1), background: "var(--viz-target)" }}
								/>
							)}
						</div>
					</li>
				);
			})}
		</ul>
	);
}
